import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { encryptPassword } from '@/lib/crypto';
import { renderGoogleButton } from '@/lib/google';
import AuthLegalConsent from '@/components/auth/AuthLegalConsent';

type Role = 'learner' | 'mentor';

const getPasswordStrength = (pwd: string) => {
  if (!pwd) return 0;
  let score = 0;
  if (pwd.length >= 8) score += 1;
  if (/[A-Z]/.test(pwd)) score += 1;
  if (/[0-9]/.test(pwd)) score += 1;
  if (/[^A-Za-z0-9]/.test(pwd)) score += 1;
  return score;
};

const strengthConfig = [
  { label: '太弱', color: 'bg-red-400' },
  { label: '較弱', color: 'bg-orange-400' },
  { label: '中等', color: 'bg-yellow-400' },
  { label: '強', color: 'bg-emerald-400' },
  { label: '非常強', color: 'bg-emerald-500' }
];

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [role, setRole] = useState<Role>('learner');
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [publicKey, setPublicKey] = useState('');
  const navigate = useNavigate();
  const { completeLogin, isAuthenticated } = useAuth();
  const googleButtonRef = useRef<HTMLDivElement | null>(null);

  const strength = useMemo(() => getPasswordStrength(password), [password]);

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/account', { replace: true });
      return;
    }
    void api
      .getWebsiteInfo()
      .then((website) => setPublicKey(website.rsaLoginPublicKey || ''))
      .catch((requestError) => {
        setError(requestError instanceof Error ? requestError.message : '初始化失敗');
      });
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    if (!countdown) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCountdown((value) => value - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  useEffect(() => {
    if (!googleButtonRef.current || isAuthenticated) {
      return;
    }

    let active = true;
    setGoogleLoading(true);
    void renderGoogleButton({
      container: googleButtonRef.current,
      text: 'signup_with',
      onCredential: async (googleToken) => {
        if (!active) {
          return;
        }
        if (!agreed) {
          setError('請先閱讀並同意服務條款、私隱政策與退款政策');
          setGoogleLoading(false);
          return;
        }
        setGoogleLoading(true);
        setError('');
        try {
          const result = await api.googleLogin({ googleToken });
          await completeLogin(result);
          navigate('/account', { replace: true });
        } catch (requestError) {
          setError(requestError instanceof Error ? requestError.message : 'Google 註冊失敗');
          setGoogleLoading(false);
        }
      },
      onError: (requestError) => {
        if (!active) {
          return;
        }
        setError(requestError.message);
        setGoogleLoading(false);
      }
    })
      .catch((requestError) => {
        if (!active) {
          return;
        }
        setError(requestError instanceof Error ? requestError.message : 'Google 註冊初始化失敗');
      })
      .finally(() => {
        if (active) {
          setGoogleLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [agreed, completeLogin, isAuthenticated, navigate]);

  const handleSendCode = () => {
    void (async () => {
      if (!email) {
        setError('請先填寫信箱地址');
        return;
      }
      setSendingCode(true);
      setError('');
      try {
        await api.sendEmailCode({ email });
        setCountdown(60);
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '驗證碼發送失敗');
      } finally {
        setSendingCode(false);
      }
    })();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void (async () => {
      if (!name.trim()) { setError('請填寫你的姓名'); return; }
      if (!email) { setError('請填寫信箱地址'); return; }
      if (!code.trim()) { setError('請輸入郵件驗證碼'); return; }
      if (password.length < 8) { setError('密碼至少需要 8 位'); return; }
      if (password !== confirmPassword) { setError('兩次密碼輸入不一致'); return; }
      if (!agreed) { setError('請先閱讀並同意服務條款、私隱政策與退款政策'); return; }
      if (!publicKey) { setError('公鑰尚未載入完成'); return; }

      setLoading(true);
      setError('');
      try {
        const mobilePwdEncrypt = await encryptPassword(password, publicKey);
        const result = await api.register({ email, code, mobilePwdEncrypt });
        await completeLogin(result);
        setSuccess(true);
      } catch (requestError) {
        setError(requestError instanceof Error ? requestError.message : '註冊失敗');
      } finally {
        setLoading(false);
      }
    })();
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white px-6">
        <div className="w-full max-w-sm text-center">
          <div className="w-20 h-20 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-6">
            <i className="ri-checkbox-circle-line text-emerald-500 text-4xl" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">帳號創建成功</h2>
          <p className="text-slate-500 text-sm mb-8 leading-relaxed">
            歡迎加入 NexusGo，<span className="font-semibold text-slate-700">{name}</span>
          </p>
          <Link
            to="/account"
            className="block w-full py-3 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition-colors cursor-pointer mb-3 whitespace-nowrap"
          >
            前往我的帳戶
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <div
        className="hidden lg:flex lg:w-[48%] relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #1e1b4b 0%, #3b0764 45%, #6d28d9 100%)' }}
      >
        <div className="relative z-10 flex flex-col justify-between p-12 w-full text-white">
          <Link to="/" className="flex items-center gap-3 cursor-pointer w-fit">
            <img
              src="/assets/readdy/static-358c7e6691b9b58b5179c58a2715558b.jpg"
              alt="NexusGo"
              className="w-10 h-10 rounded-xl object-cover"
            />
            <span className="font-bold text-xl tracking-tight">NexusGo</span>
          </Link>

          <div>
            <div className="w-14 h-1 bg-violet-300 rounded-full mb-6" />
            <h2 className="text-4xl font-bold leading-snug mb-3">
              開啟你的成長之旅
              <br />
              從第一步開始
            </h2>
            <p className="text-violet-200 text-sm leading-relaxed max-w-xs">
              註冊後即可直接對接真實後端帳號體系，課程、訂單與學習資料都會落到實際資料庫。
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center px-6 py-10 bg-white overflow-y-auto">
        <div className="w-full max-w-[420px]">
          <div className="mb-7">
            <h1 className="text-2xl font-bold text-slate-900 mb-1.5">建立你的帳號</h1>
            <p className="text-slate-500 text-sm">
              已有帳號？{' '}
              <Link to="/login" className="text-violet-600 font-medium hover:text-violet-700 cursor-pointer">
                立即登入
              </Link>
            </p>
          </div>

          <div className="mb-5">
            <div
              ref={googleButtonRef}
              className={`min-h-11 transition-opacity ${googleLoading ? 'opacity-70' : 'opacity-100'}`}
            />
            {googleLoading && (
              <div className="mt-2 flex items-center justify-center gap-2 text-xs text-slate-500">
                <i className="ri-loader-4-line animate-spin" />
                正在初始化 Google 註冊
              </div>
            )}
          </div>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-slate-100" />
            <span className="text-slate-400 text-xs">或填寫資料註冊</span>
            <div className="flex-1 h-px bg-slate-100" />
          </div>

          {error && (
            <div className="flex items-start gap-2.5 px-4 py-3.5 bg-amber-50 border border-amber-200 rounded-xl mb-4">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-information-line text-amber-500 text-sm" />
              </div>
              <div className="text-xs text-amber-800 leading-relaxed">{error}</div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">我是</label>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'learner', icon: 'ri-graduation-cap-line', label: '求職學員', desc: '尋找導師指導' },
                  { value: 'mentor', icon: 'ri-user-star-line', label: '產業導師', desc: '分享經驗帶學員' }
                ] as { value: Role; icon: string; label: string; desc: string }[]).map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setRole(option.value)}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border-2 transition-all duration-150 cursor-pointer ${
                      role === option.value
                        ? 'border-violet-500 bg-violet-50'
                        : 'border-slate-200 hover:border-slate-300 bg-white'
                    }`}
                  >
                    <div className={`w-6 h-6 flex items-center justify-center ${role === option.value ? 'text-violet-600' : 'text-slate-400'}`}>
                      <i className={`${option.icon} text-base`} />
                    </div>
                    <span className={`text-xs font-semibold ${role === option.value ? 'text-violet-700' : 'text-slate-600'}`}>{option.label}</span>
                    <span className="text-slate-400 text-xs">{option.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">姓名</label>
              <input
                type="text"
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="你的姓名"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">信箱地址</label>
              <input
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                placeholder="your@email.com"
                className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">郵件驗證碼</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={code}
                  onChange={(event) => setCode(event.target.value)}
                  placeholder="輸入信箱收到的驗證碼"
                  className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
                <button
                  type="button"
                  disabled={sendingCode || countdown > 0}
                  onClick={handleSendCode}
                  className="px-4 py-2.5 bg-slate-100 text-slate-700 rounded-lg text-sm font-semibold hover:bg-slate-200 transition-colors disabled:opacity-50"
                >
                  {countdown > 0 ? `${countdown}s` : sendingCode ? '發送中...' : '發送驗證碼'}
                </button>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">設定密碼</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder="至少 8 位"
                  className="w-full px-4 py-2.5 border border-slate-200 rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:border-violet-400 focus:ring-2 focus:ring-violet-100 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600"
                >
                  <i className={`${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4].map((index) => (
                      <div
                        key={index}
                        className={`flex-1 h-1 rounded-full transition-all duration-300 ${
                          index <= strength + 1 ? strengthConfig[strength].color : 'bg-slate-100'
                        }`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs ${strength <= 1 ? 'text-red-500' : strength <= 2 ? 'text-yellow-600' : 'text-emerald-600'}`}>
                    密碼強度：{strengthConfig[strength]?.label}
                  </p>
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">確認密碼</label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(event) => setConfirmPassword(event.target.value)}
                  placeholder="再次輸入密碼"
                  className={`w-full px-4 py-2.5 border rounded-lg text-sm text-slate-900 placeholder-slate-400 focus:outline-none focus:ring-2 transition-all ${
                    confirmPassword && password !== confirmPassword
                      ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                      : confirmPassword && password === confirmPassword
                        ? 'border-emerald-300 focus:border-emerald-400 focus:ring-emerald-100'
                        : 'border-slate-200 focus:border-violet-400 focus:ring-violet-100'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center cursor-pointer text-slate-400 hover:text-slate-600"
                >
                  <i className={`${showConfirm ? 'ri-eye-off-line' : 'ri-eye-line'} text-sm`} />
                </button>
              </div>
            </div>

            <AuthLegalConsent checked={agreed} onChange={setAgreed} compact />

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-violet-600 text-white rounded-lg text-sm font-semibold hover:bg-violet-700 transition-all duration-200 cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <i className="ri-loader-4-line animate-spin" />
                  註冊中...
                </>
              ) : (
                '免費建立帳號'
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
